/**
 * Iter 189 — StorefrontCheckoutPage (embedded Stripe Payment Element).
 * ============================================================================
 *
 * On-site checkout for the public storefront:
 *   • Cart summary (line items + total)
 *   • Email capture (fires abandoned-cart capture automatically via CartContext)
 *   • Shipping address form (name + street + city + zip + country)
 *   • <PaymentElement /> — cards + Apple Pay + Google Pay auto-rendered by Stripe
 *   • On success: Stripe redirects back → we poll /order/{id} → show receipt state
 *
 * Route: `/checkout` (customer navigates here from CartDrawer).
 *
 * Key contract with the backend:
 *   POST /api/public/storefront-checkout/intent    → { order_id, client_secret, publishable_key }
 *   GET  /api/public/storefront-checkout/order/{order_id} → { status, ... }
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ShoppingBag, ChevronLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '@/context/CartContext';
import AbandonedCartCapture from '@/components/storefront/AbandonedCartCapture';
import StorefrontPaymentMethods from '@/components/storefront/StorefrontPaymentMethods';
import TrustBadges from '@/components/storefront/TrustBadges';
import TurnstileWidget from '@/components/TurnstileWidget';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Cache Stripe.js promise per publishable key — MUST be created outside render.
const stripePromiseCache = {};
function getStripePromise(pubKey) {
  if (!pubKey) return null;
  if (!stripePromiseCache[pubKey]) stripePromiseCache[pubKey] = loadStripe(pubKey);
  return stripePromiseCache[pubKey];
}

function inferSubdomain() {
  const host = window.location.hostname;
  if (!host.includes('emergentagent.com') && !host.startsWith('localhost')) return host.split('.')[0];
  const m = window.location.pathname.match(/^\/site\/([^/]+)/);
  return m ? m[1] : '';
}

/**
 * Sub-component: form that renders <PaymentElement /> + submit button.
 * MUST be a child of <Elements/> so it can useStripe() + useElements().
 */
function CheckoutForm({ orderId, totalCents, onProcessing }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  // Iter 189e — track whether Apple/Google Pay Express Checkout is actually
  // renderable in this browser. `onReady` fires with { availablePaymentMethods }
  // — if none are available we hide the divider and container entirely.
  const [expressReady, setExpressReady] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setErrMsg('');
    setSubmitting(true);
    onProcessing?.(true);
    // Use returnUrl on our own page — we handle the post-payment UI ourselves
    const returnUrl = `${window.location.origin}${window.location.pathname}?status=success&order_id=${orderId}`;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (error) {
      setErrMsg(error.message || 'Payment failed. Try a different card.');
      setSubmitting(false);
      onProcessing?.(false);
      return;
    }
    // If no redirect required (e.g. non-3DS card), poll the order manually
    window.location.assign(returnUrl);
  }, [stripe, elements, orderId, onProcessing]);

  // Express Checkout confirms directly (no separate submit) — Stripe handles
  // the Apple Pay / Google Pay sheet and returns after the shopper approves.
  const handleExpressConfirm = useCallback(async () => {
    if (!stripe || !elements) return;
    onProcessing?.(true);
    const returnUrl = `${window.location.origin}${window.location.pathname}?status=success&order_id=${orderId}`;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (error) {
      setErrMsg(error.message || 'Wallet payment failed.');
      onProcessing?.(false);
      return;
    }
    window.location.assign(returnUrl);
  }, [stripe, elements, orderId, onProcessing]);

  return (
    <form onSubmit={handleSubmit} data-testid="checkout-payment-form" className="space-y-5">
      {/* Iter 189e — Prominent Apple Pay / Google Pay express buttons on top */}
      <div
        className={expressReady ? 'p-4 rounded-xl' : 'hidden'}
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(167,139,250,0.18)' }}
        data-testid="checkout-express"
      >
        <ExpressCheckoutElement
          onReady={(e) => {
            const avail = e?.availablePaymentMethods || {};
            setExpressReady(Object.values(avail).some(Boolean));
          }}
          onConfirm={handleExpressConfirm}
          options={{ buttonHeight: 44, buttonType: { applePay: 'pay', googlePay: 'pay' } }}
        />
      </div>
      {expressReady && (
        <div className="flex items-center gap-3 text-[10px] text-white/45 uppercase tracking-[0.22em] font-bold" data-testid="checkout-express-divider">
          <span className="flex-1 h-px bg-white/12" />
          <span>Or pay with card</span>
          <span className="flex-1 h-px bg-white/12" />
        </div>
      )}
      <div
        className="p-5 rounded-xl"
        style={{
          background: 'linear-gradient(180deg, #FEFEFE 0%, #F8F7FC 100%)',
          border: '1px solid rgba(167,139,250,0.18)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        }}
      >
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      {errMsg && <p className="text-xs text-red-300" data-testid="checkout-err">{errMsg}</p>}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full py-4 rounded-full text-sm font-bold uppercase tracking-[0.18em] text-white disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-all hover:shadow-[0_10px_40px_rgba(167,139,250,0.5)]"
        style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 50%, #EC4899 100%)' }}
        data-testid="checkout-pay"
      >
        <Lock size={13} />
        {submitting ? 'Processing…' : `Pay $${(totalCents / 100).toFixed(2)}`}
      </button>
      <p className="text-[10px] text-white/45 text-center tracking-wide">
        Card, Apple Pay &amp; Google Pay supported &middot; Secured by Stripe &middot; Zero platform fees.
      </p>
    </form>
  );
}

export default function StorefrontCheckoutPage() {
  const navigate = useNavigate();
  const { items, total, customerEmail, setCustomerEmail, markCheckoutComplete } = useCart();

  // Post-payment (?status=success) handling
  const [phase, setPhase] = useState('cart'); // 'cart' | 'pay' | 'success' | 'error'
  const [orderId, setOrderId] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [totalCents, setTotalCents] = useState(0);
  const [shipping, setShipping] = useState({ name: '', street: '', city: '', zip: '', country: 'US' });
  const [creating, setCreating] = useState(false);
  const [errBanner, setErrBanner] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [checkoutCfg, setCheckoutCfg] = useState(null);
  const [additionalValues, setAdditionalValues] = useState({});
  const [emailOptIn, setEmailOptIn] = useState(false);
  // Cloudflare Turnstile token (anti-abuse guard — required by backend)
  const [turnstileToken, setTurnstileToken] = useState('');
  const countryTouchedRef = useRef(false);
  const polledRef = useRef(false);

  // Iter 191r — fetch vendor's Checkout settings snapshot
  const subdomainForCfg = useMemo(() => inferSubdomain(), []);
  useEffect(() => {
    if (!subdomainForCfg) return;
    axios.get(`${API}/public/sites/${subdomainForCfg}/checkout-config`)
      .then(({ data }) => {
        setCheckoutCfg(data);
        // Only pre-fill country if the buyer hasn't manually picked one yet
        if (!countryTouchedRef.current && data.default_country) {
          setShipping((s) => ({ ...s, country: data.default_country }));
        }
        setEmailOptIn(!!data.email_signup?.checked_by_default);
      })
      .catch(() => { /* first-time / site missing */ });
  }, [subdomainForCfg]);

  // ─────────────────────────────────────────────────────────────
  // Iter 191w — Auto-populate from signed-in buyer's default address
  // If the buyer is signed in on this vendor's storefront, read their
  // default_shipping address and email and pre-fill the checkout form.
  // Fields the buyer has already typed into are preserved.
  // ─────────────────────────────────────────────────────────────
  const [signedInBuyer, setSignedInBuyer] = useState(null);
  const [prefillDismissed, setPrefillDismissed] = useState(false);
  const shippingTouchedRef = useRef(false);
  useEffect(() => {
    if (!subdomainForCfg) return;
    const token = localStorage.getItem(`entrepren_buyer_session:${subdomainForCfg}`);
    if (!token) return;
    const H = { Authorization: `Bearer ${token}` };
    let cancelled = false;
    (async () => {
      try {
        const [{ data: me }, { data: addr }] = await Promise.all([
          axios.get(`${API}/public/buyers/${subdomainForCfg}/me`, { headers: H }),
          axios.get(`${API}/public/buyers/${subdomainForCfg}/addresses`, { headers: H }),
        ]);
        if (cancelled) return;
        setSignedInBuyer(me);
        // Fill email if empty
        if (!customerEmail && me?.email && setCustomerEmail) {
          setCustomerEmail(me.email);
        }
        // Fill shipping if the buyer hasn't touched the form yet.
        if (!shippingTouchedRef.current) {
          const def = (addr.addresses || []).find((a) => a.is_default_shipping) || (addr.addresses || [])[0];
          if (def) {
            setShipping((s) => ({
              ...s,
              name:    def.name || me?.name || s.name,
              street:  def.line1 + (def.line2 ? `, ${def.line2}` : ''),
              city:    def.city || s.city,
              zip:     def.postal || s.zip,
              country: def.country || s.country,
            }));
            countryTouchedRef.current = true; // don't stomp the buyer's saved country
          } else if (me?.name && !shipping.name) {
            setShipping((s) => ({ ...s, name: me.name }));
          }
        }
      } catch (_) {
        // Silent — invalid session, or something transient.
      }
    })();
    return () => { cancelled = true; };
  }, [subdomainForCfg]);

  // Handle Stripe redirect return + e-Transfer / PayPal / Square post-payment
  useEffect(() => {
    const url = new URL(window.location.href);
    const status = url.searchParams.get('status');
    const ordId = url.searchParams.get('order_id');
    if (status === 'pending' && ordId) {
      polledRef.current = true;
      setPhase('success');
      setOrderId(ordId);
      toast.success('Order recorded — awaiting payment confirmation.');
      try { markCheckoutComplete(); } catch { /* swallow */ }
      return;
    }
    if (status === 'success' && ordId && !polledRef.current) {
      polledRef.current = true;
      setPhase('pay'); // temporarily
      setOrderId(ordId);
      (async () => {
        // Poll order status up to 5 times, 2s apart
        for (let i = 0; i < 5; i++) {
          try {
            const { data } = await axios.get(`${API}/public/storefront-checkout/order/${ordId}`);
            if (data.status === 'paid') {
              setPhase('success');
              setTotalCents(data.total_cents || 0);
              try { await markCheckoutComplete(); } catch { /* swallow */ }
              return;
            }
          } catch { /* swallow, keep polling */ }
          await new Promise(r => setTimeout(r, 2000));
        }
        // Still not confirmed → show pending message
        setPhase('success');
        toast.info('Payment received — order confirmation coming shortly.');
      })();
    }
  }, []);

  const startCheckout = useCallback(async () => {
    if (!customerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (items.length === 0) { toast.error('Your cart is empty'); return; }
    // Iter 191r — enforce minimum order gate
    if (checkoutCfg?.minimum_order_enabled && total < checkoutCfg.minimum_order_amount) {
      toast.error(`Add $${(checkoutCfg.minimum_order_amount - total).toFixed(2)} more to reach the minimum order.`);
      return;
    }
    // Iter 191r — enforce required additional fields
    const missing = (checkoutCfg?.additional_fields || []).find((f) => {
      if (!f.required) return false;
      const v = additionalValues[f.id];
      return f.type === 'checkbox' ? !v : !String(v || '').trim();
    });
    if (missing) { toast.error(`"${missing.label}" is required.`); return; }
    setErrBanner('');
    setCreating(true);
    try {
      const site_subdomain = inferSubdomain();
      const { data } = await axios.post(`${API}/public/storefront-checkout/intent`, {
        site_subdomain,
        customer_email: customerEmail,
        shipping,
        items: items.map(it => ({
          product_id: it.product_id,
          name: it.name,
          qty: it.quantity,
          price: it.price,
          size: it.size,
          image: it.image,
        })),
        // Iter 191r — extras the vendor asked us to collect at checkout
        email_opt_in: emailOptIn,
        additional_fields: additionalValues,
        // Iter 202 — Cloudflare Turnstile token for anti-abuse guard.
        turnstile_token: turnstileToken,
      });
      setOrderId(data.order_id);
      setClientSecret(data.client_secret);
      setPublishableKey(data.publishable_key);
      setTotalCents(data.amount_cents);
      setPhase('pay');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start checkout — try again.';
      setErrBanner(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }, [customerEmail, items, shipping, checkoutCfg, total, additionalValues, emailOptIn, turnstileToken]);

  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  // ─────────────────────────────────────────────────────────────
  // Iter 191w — Auto-save the shipping address to the signed-in
  // buyer's book when the order successfully completes (only if
  // the vendor's `auto_save_addresses` toggle is on).
  // ─────────────────────────────────────────────────────────────
  const autoSaveDoneRef = useRef(false);
  useEffect(() => {
    if (phase !== 'success') return;
    if (autoSaveDoneRef.current) return;
    if (!subdomainForCfg) return;
    const token = localStorage.getItem(`entrepren_buyer_session:${subdomainForCfg}`);
    if (!token) return;
    if (!signedInBuyer) return;
    // Only save when the vendor opted in (buyer_public_config exposes this)
    (async () => {
      try {
        const { data: cfg } = await axios.get(`${API}/public/buyers/${subdomainForCfg}/config`);
        if (!cfg?.auto_save_addresses) return;
        if (!shipping.street || !shipping.city) return;
        // Check for a duplicate — don't spam the buyer's book
        const H = { Authorization: `Bearer ${token}` };
        const { data: existing } = await axios.get(`${API}/public/buyers/${subdomainForCfg}/addresses`, { headers: H });
        const dupe = (existing.addresses || []).some((a) =>
          (a.line1 || '').trim() === shipping.street.trim() &&
          (a.postal || '').trim() === shipping.zip.trim() &&
          (a.city || '').trim() === shipping.city.trim(),
        );
        if (dupe) { autoSaveDoneRef.current = true; return; }
        const isFirst = (existing.addresses || []).length === 0;
        await axios.post(`${API}/public/buyers/${subdomainForCfg}/addresses`, {
          label: 'From last order',
          name:  shipping.name,
          line1: shipping.street,
          city:  shipping.city,
          postal: shipping.zip,
          country: shipping.country,
          is_default_shipping: isFirst,
        }, { headers: H });
        autoSaveDoneRef.current = true;
      } catch (_) { /* silent */ }
    })();
  }, [phase, subdomainForCfg, signedInBuyer, shipping]);

  // ── Success screen ───────────────────────────────────────────
  if (phase === 'success') {
    return (
      <div className="min-h-screen text-white flex items-center justify-center px-6 relative overflow-hidden" style={{ background: '#07071A' }} data-testid="storefront-checkout-success">
        <div className="pointer-events-none absolute inset-0 opacity-70" aria-hidden="true">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.18) 0%, rgba(139,92,246,0.14) 40%, transparent 70%)' }} />
        </div>
        <div
          className="relative max-w-md w-full rounded-2xl p-10 text-center"
          style={{
            background: 'linear-gradient(180deg, rgba(20,15,45,0.75) 0%, rgba(14,14,35,0.9) 100%)',
            border: '1px solid rgba(167,139,250,0.22)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
        >
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))', border: '1px solid rgba(16,185,129,0.35)' }}>
            <CheckCircle2 size={30} className="text-emerald-400" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-400">Payment received</p>
          <h1 className="mt-3 text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Thank you.</h1>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">
            Your order is confirmed. A branded receipt is on its way to your inbox.
          </p>
          {orderId && <p className="text-[11px] mt-5 font-mono text-white/35">Order #{orderId}</p>}
          <button
            onClick={() => navigate(-1)}
            className="mt-8 w-full py-3.5 rounded-full text-xs font-bold uppercase tracking-[0.18em] text-white transition-all hover:shadow-[0_10px_40px_rgba(167,139,250,0.5)]"
            style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 50%, #EC4899 100%)' }}
            data-testid="checkout-success-back"
          >
            Continue shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative overflow-hidden" data-testid="storefront-checkout" style={{ background: '#07071A' }}>
      {/* Ambient glow — twin violet radial halos to add depth without noise */}
      <div className="pointer-events-none absolute inset-0 opacity-70" aria-hidden="true">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.28) 0%, rgba(139,92,246,0) 65%)' }} />
        <div className="absolute -bottom-40 -right-40 w-[620px] h-[620px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.22) 0%, rgba(236,72,153,0) 65%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(139,92,246,0.12) 0%, transparent 55%)' }} />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8" data-testid="checkout-back">
          <ChevronLeft size={16} /> Back
        </button>

        <div className="mb-10 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#A78BFA]">Secure checkout</p>
            <h1 className="mt-2 text-4xl md:text-5xl font-bold leading-none" style={{ fontFamily: 'Syne, sans-serif' }}>Checkout</h1>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[11px] text-white/50">
            <Lock size={12} /> 256-bit encrypted &middot; Powered by Stripe
          </div>
        </div>

        {errBanner && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-sm" data-testid="checkout-banner-err">{errBanner}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* ── Order summary ─────────────────────────────────── */}
          <div className="order-2 md:order-1 md:col-span-2">
            <div
              className="rounded-2xl p-6 h-full"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(167,139,250,0.18)',
                backdropFilter: 'blur(16px)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
              }}
            >
              <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#A78BFA] mb-5">Order Summary</p>
              {items.length === 0 ? (
                <div className="py-16 text-center text-white/40" data-testid="checkout-empty">
                  <ShoppingBag size={30} className="mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((it, i) => (
                    <div key={`${it.product_id}-${i}`} className="flex items-center gap-3 pb-3 border-b border-white/8" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                      {it.image ? (
                        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-14 h-14 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}>
                          <ShoppingBag size={16} className="text-white/80" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{it.name}</p>
                        <p className="text-xs text-white/50 mt-0.5">Qty {it.quantity}{it.size ? ` · ${it.size}` : ''}</p>
                      </div>
                      <p className="text-sm font-bold whitespace-nowrap">${(it.price * it.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                  <div className="pt-2 space-y-2">
                    <div className="flex items-center justify-between text-sm text-white/70">
                      <span>Subtotal</span>
                      <span className="font-medium text-white" data-testid="checkout-subtotal">${total.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-white/45">
                      <span>Shipping</span>
                      <span>Calculated separately</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                    <span className="text-sm text-white/60 uppercase tracking-wider font-bold">Total</span>
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="checkout-total">${total.toFixed(2)}</span>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-[10px] text-white/45 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Zero platform fees &middot; 100% to the maker
                  </div>
                </div>
              )}
            </div>

            {/* Trust badges — reduce checkout abandonment */}
            <div className="mt-4">
              <TrustBadges />
            </div>
          </div>

          {/* ── Form / Payment ────────────────────────────────── */}
          <div className="order-1 md:order-2 md:col-span-3">
            <div
              className="rounded-2xl p-7 space-y-5"
              style={{
                background: 'linear-gradient(180deg, rgba(20,15,45,0.75) 0%, rgba(14,14,35,0.9) 100%)',
                border: '1px solid rgba(167,139,250,0.22)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
              }}
            >
              {phase === 'cart' && (
                <>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#A78BFA] mb-3">Contact</p>
                    <AbandonedCartCapture />
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#A78BFA] mb-3">Shipping address</p>
                    {signedInBuyer && !prefillDismissed && (
                      <p className="text-[11px] text-white/60 mb-3" data-testid="checkout-prefilled-hint">
                        Pre-filled from your saved address. <button type="button" onClick={() => { shippingTouchedRef.current = false; setPrefillDismissed(true); setShipping({ name: '', street: '', city: '', zip: '', country: shipping.country }); }} className="text-[#A78BFA] hover:text-white underline underline-offset-2">Use a different address</button>
                      </p>
                    )}
                    <div className="grid grid-cols-1 gap-2.5">
                      <input value={shipping.name} onChange={(e) => { shippingTouchedRef.current = true; setShipping({ ...shipping, name: e.target.value }); }} placeholder="Full name" className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/30 focus:border-[#A78BFA]/60 focus:bg-white/[0.06] outline-none transition-all" data-testid="checkout-name" />
                      <input value={shipping.street} onChange={(e) => { shippingTouchedRef.current = true; setShipping({ ...shipping, street: e.target.value }); }} placeholder="Street address" className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/30 focus:border-[#A78BFA]/60 focus:bg-white/[0.06] outline-none transition-all" data-testid="checkout-street" />
                      <div className="grid grid-cols-3 gap-2.5">
                        <input value={shipping.city} onChange={(e) => { shippingTouchedRef.current = true; setShipping({ ...shipping, city: e.target.value }); }} placeholder="City" className="col-span-2 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/30 focus:border-[#A78BFA]/60 focus:bg-white/[0.06] outline-none transition-all" data-testid="checkout-city" />
                        <input value={shipping.zip} onChange={(e) => { shippingTouchedRef.current = true; setShipping({ ...shipping, zip: e.target.value }); }} placeholder="ZIP" className="px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/30 focus:border-[#A78BFA]/60 focus:bg-white/[0.06] outline-none transition-all" data-testid="checkout-zip" />
                      </div>
                      <select value={shipping.country} onChange={(e) => { shippingTouchedRef.current = true; countryTouchedRef.current = true; setShipping({ ...shipping, country: e.target.value }); }} className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white focus:border-[#A78BFA]/60 focus:bg-white/[0.06] outline-none transition-all" data-testid="checkout-country">
                        {['US','CA','MX','GB','AU','NZ','FR','DE','ES','IT','JP','BR'].map((c) => <option key={c} value={c} style={{ background: '#0E0E23' }}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Iter 191r — Vendor-defined additional fields */}
                  {checkoutCfg?.additional_fields?.length > 0 && (
                    <div data-testid="checkout-additional-fields">
                      <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#A78BFA] mb-3">Additional information</p>
                      <div className="space-y-2.5">
                        {checkoutCfg.additional_fields.map((f) => {
                          const val = additionalValues[f.id] ?? (f.type === 'checkbox' ? false : '');
                          const set = (v) => setAdditionalValues((a) => ({ ...a, [f.id]: v }));
                          const cls = 'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/30 focus:border-[#A78BFA]/60 focus:bg-white/[0.06] outline-none';
                          if (f.type === 'textarea') return (
                            <label key={f.id} className="block text-[11px] text-white/60">
                              <span>{f.label}{f.required && <span className="text-rose-300"> *</span>}</span>
                              <textarea rows={2} value={val} onChange={(e) => set(e.target.value)} className={`mt-1 ${cls} resize-y`} data-testid={`checkout-field-${f.id}`} />
                            </label>
                          );
                          if (f.type === 'select') return (
                            <label key={f.id} className="block text-[11px] text-white/60">
                              <span>{f.label}{f.required && <span className="text-rose-300"> *</span>}</span>
                              <select value={val} onChange={(e) => set(e.target.value)} className={`mt-1 ${cls}`} data-testid={`checkout-field-${f.id}`}>
                                <option value="" style={{ background: '#0E0E23' }}>—</option>
                                {(f.options || []).map((o) => <option key={o} value={o} style={{ background: '#0E0E23' }}>{o}</option>)}
                              </select>
                            </label>
                          );
                          if (f.type === 'checkbox') return (
                            <label key={f.id} className="flex items-center gap-2 text-[13px] text-white/85" data-testid={`checkout-field-${f.id}`}>
                              <input type="checkbox" checked={!!val} onChange={(e) => set(e.target.checked)} /> {f.label}{f.required && <span className="text-rose-300"> *</span>}
                            </label>
                          );
                          return (
                            <label key={f.id} className="block text-[11px] text-white/60">
                              <span>{f.label}{f.required && <span className="text-rose-300"> *</span>}</span>
                              <input value={val} onChange={(e) => set(e.target.value)} placeholder={f.label} className={`mt-1 ${cls}`} data-testid={`checkout-field-${f.id}`} />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Iter 191r — Email signup opt-in */}
                  {checkoutCfg?.email_signup?.enabled && (
                    <label className="flex items-start gap-2 text-[12px] text-white/80" data-testid="checkout-email-optin">
                      <input type="checkbox" checked={emailOptIn} onChange={(e) => setEmailOptIn(e.target.checked)} className="mt-1" />
                      <span>
                        {checkoutCfg.email_signup.checkbox_label}
                        {checkoutCfg.email_signup.consent_text && (
                          <span className="block mt-0.5 text-[10.5px] text-white/45">{checkoutCfg.email_signup.consent_text}</span>
                        )}
                      </span>
                    </label>
                  )}

                  {/* Iter 191r — Minimum order gate */}
                  {checkoutCfg?.minimum_order_enabled && total < checkoutCfg.minimum_order_amount && (
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs" data-testid="checkout-min-order-warn">
                      Minimum order is <b>${checkoutCfg.minimum_order_amount.toFixed(2)} {checkoutCfg.minimum_order_currency}</b>. Add ${(checkoutCfg.minimum_order_amount - total).toFixed(2)} more to continue.
                    </div>
                  )}

                  <TurnstileWidget onToken={setTurnstileToken} action="storefront_checkout" className="pt-1" />

                  <button
                    onClick={startCheckout}
                    disabled={creating || items.length === 0 || !turnstileToken || (checkoutCfg?.minimum_order_enabled && total < checkoutCfg.minimum_order_amount)}
                    className="w-full py-4 rounded-full text-sm font-bold uppercase tracking-[0.18em] text-white disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-all hover:shadow-[0_10px_40px_rgba(167,139,250,0.5)]"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 50%, #EC4899 100%)' }}
                    data-testid="checkout-continue"
                  >
                    {creating ? <><Loader2 size={14} className="animate-spin" /> Preparing…</> : <><Lock size={13} /> Continue to payment</>}
                  </button>

                  {/* Iter 191r — Vendor's store policies */}
                  {checkoutCfg?.store_policies && (checkoutCfg.store_policies.returns || checkoutCfg.store_policies.tos || checkoutCfg.store_policies.privacy) && (
                    <div className="pt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] text-white/45 tracking-wide" data-testid="checkout-policies">
                      {checkoutCfg.store_policies.returns && <a href="#" onClick={(e) => { e.preventDefault(); toast(checkoutCfg.store_policies.returns, { duration: 8000 }); }} className="underline hover:text-white">Returns</a>}
                      {checkoutCfg.store_policies.tos && <a href="#" onClick={(e) => { e.preventDefault(); toast(checkoutCfg.store_policies.tos, { duration: 8000 }); }} className="underline hover:text-white">Terms of service</a>}
                      {checkoutCfg.store_policies.privacy && <a href="#" onClick={(e) => { e.preventDefault(); toast(checkoutCfg.store_policies.privacy, { duration: 8000 }); }} className="underline hover:text-white">Privacy</a>}
                    </div>
                  )}
                </>
              )}

              {phase === 'pay' && clientSecret && stripePromise && (
                <>
                  <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#A78BFA] mb-2">Payment</p>
                  <p className="text-xs text-white/60 mb-4">Paying <span className="text-white font-semibold">${(totalCents / 100).toFixed(2)}</span> to <span className="text-white font-semibold">{inferSubdomain()}</span></p>

                  <StorefrontPaymentMethods
                    subdomain={inferSubdomain()}
                    amount={totalCents / 100}
                    currency="USD"
                    customerEmail={customerEmail}
                    items={items}
                    method={paymentMethod}
                    onMethodChange={setPaymentMethod}
                    onPaid={(altOrderId, opts) => {
                      const returnUrl = `${window.location.origin}${window.location.pathname}?status=${opts?.pending ? 'pending' : 'success'}&order_id=${altOrderId || orderId}`;
                      window.location.assign(returnUrl);
                    }}
                    renderStripe={() => (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret,
                          appearance: {
                            theme: 'flat',
                            variables: {
                              colorPrimary: '#A78BFA',
                              colorBackground: '#FEFEFE',
                              colorText: '#0E0E23',
                              colorTextSecondary: '#5B5B7A',
                              colorTextPlaceholder: '#9CA3AF',
                              colorDanger: '#F87171',
                              fontFamily: 'system-ui, -apple-system, sans-serif',
                              fontSizeBase: '14px',
                              borderRadius: '10px',
                              spacingUnit: '4px',
                            },
                            rules: {
                              '.Tab': { border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'none', padding: '10px 12px' },
                              '.Tab:hover': { border: '1px solid rgba(167,139,250,0.4)' },
                              '.Tab--selected': { border: '1px solid #A78BFA', backgroundColor: '#F5F1FF', color: '#5B21B6' },
                              '.Input': { border: '1px solid rgba(0,0,0,0.1)', padding: '11px 12px', boxShadow: 'none' },
                              '.Input:focus': { border: '1px solid #A78BFA', boxShadow: '0 0 0 3px rgba(167,139,250,0.15)' },
                              '.Label': { fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5B5B7A' },
                            },
                          },
                        }}
                      >
                        <CheckoutForm orderId={orderId} totalCents={totalCents} />
                      </Elements>
                    )}
                  />
                </>
              )}

              {phase === 'pay' && !clientSecret && (
                <div className="py-12 text-center text-white/60" data-testid="checkout-loading">
                  <Loader2 size={22} className="animate-spin mx-auto mb-3 text-[#A78BFA]" />
                  <p className="text-sm">Preparing your secure payment&hellip;</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
